/**
 * Conversion scrub / manual-review — operator UI (Feature #139).
 *
 * Composition:
 *   - Tab switcher: Queue | Audit log
 *   - Queue tab: KPI strip (today / week / pending / reviewed) + status filter
 *     + table with Approve / Reject buttons and a reject-reason modal
 *   - Audit tab: reviewed history table with filter (all / approved / rejected)
 *
 * Data layer: TanStack Query for fetch + mutation, Sonner for feedback.
 * Reject reason capture: a lightweight modal kept local to this file to stay
 * inside the feature lane. The modal focuses the textarea on open and enforces
 * the same 3-1000 character contract the API enforces.
 *
 * Brand: emerald→sky headline gradient, zinc borders, 6-8px rounded, light
 * theme only, no emojis, no competitor names.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
	AUDIT_FILTERS,
	approveScrub,
	computeScrubStats,
	fetchScrubAudit,
	fetchScrubQueue,
	formatInteger,
	formatScrubDate,
	formatScrubDateTime,
	isAuditFilter,
	isQueueFilter,
	isReasonValid,
	QUEUE_FILTERS,
	rejectScrub,
	scrubQueryKeys,
	statusLabel,
	type AuditFilter,
	type QueueFilter,
	type ScrubRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — grouped for JSX readability. Emerald/zinc brand system.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const TEXTAREA =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Tab typing — a deliberate subset so that the switcher is exhaustive.
// ---------------------------------------------------------------------------

export type ScrubTab = "queue" | "audit";
const TABS: readonly ScrubTab[] = ["queue", "audit"] as const;

export function isScrubTab(v: unknown): v is ScrubTab {
	return typeof v === "string" && (TABS as readonly string[]).includes(v);
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export interface ScrubWorkflowPageProps {
	initialTab?: ScrubTab;
	initialStatus?: QueueFilter;
}

export function ScrubWorkflowPage(props: ScrubWorkflowPageProps = {}) {
	const [tab, setTab] = useState<ScrubTab>(
		props.initialTab && isScrubTab(props.initialTab) ? props.initialTab : "queue",
	);

	return (
		<main
			data-testid="scrub-workflow-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />
			<TabSwitcher tab={tab} onChange={setTab} />
			{tab === "queue" ? (
				<QueuePane initialStatus={props.initialStatus} />
			) : (
				<AuditPane />
			)}
		</main>
	);
}

export default ScrubWorkflowPage;

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Conversions
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Scrub review queue
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Review questionable conversions one at a time. Approve clean
				conversions back into payouts, or reject with a reason — every decision
				is recorded in the audit log for downstream reporting.
			</p>
		</header>
	);
}

function TabSwitcher(props: {
	tab: ScrubTab;
	onChange: (next: ScrubTab) => void;
}) {
	return (
		<div
			role="tablist"
			aria-label="Scrub workflow tabs"
			className="inline-flex gap-1 rounded-[8px] border border-zinc-200 bg-zinc-50 p-1"
		>
			{TABS.map((t) => {
				const active = t === props.tab;
				return (
					<button
						key={t}
						type="button"
						role="tab"
						aria-selected={active}
						data-testid={`tab-${t}`}
						onClick={() => props.onChange(t)}
						className={[
							"rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors",
							active
								? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
								: "text-zinc-600 hover:text-zinc-900",
						].join(" ")}
					>
						{t === "queue" ? "Queue" : "Audit log"}
					</button>
				);
			})}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Queue pane
// ---------------------------------------------------------------------------

function QueuePane(props: { initialStatus?: QueueFilter }) {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<QueueFilter>(
		props.initialStatus && isQueueFilter(props.initialStatus)
			? props.initialStatus
			: "pending",
	);
	const [modal, setModal] = useState<{ id: string; conversion_id: string } | null>(
		null,
	);

	const queueQuery = useQuery({
		queryKey: scrubQueryKeys.queue(status),
		queryFn: () => fetchScrubQueue({ status }),
	});

	const rows: readonly ScrubRow[] = queueQuery.data?.items ?? [];
	const stats = useMemo(() => computeScrubStats(rows), [rows]);

	const invalidateAll = useCallback(() => {
		for (const f of QUEUE_FILTERS) {
			queryClient.invalidateQueries({ queryKey: scrubQueryKeys.queue(f) });
		}
		for (const f of AUDIT_FILTERS) {
			queryClient.invalidateQueries({ queryKey: scrubQueryKeys.audit(f) });
		}
	}, [queryClient]);

	const approveMutation = useMutation({
		mutationFn: approveScrub,
		onSuccess: (_res, vars) => {
			toast.success("Conversion approved.");
			removeRowLocally(queryClient, status, vars.id);
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to approve conversion.");
		},
	});

	const rejectMutation = useMutation({
		mutationFn: rejectScrub,
		onSuccess: (_res, vars) => {
			toast.success("Conversion rejected.");
			removeRowLocally(queryClient, status, vars.id);
			invalidateAll();
			setModal(null);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to reject conversion.");
		},
	});

	const busy = approveMutation.isPending || rejectMutation.isPending;

	const handleApprove = useCallback(
		(row: ScrubRow) => {
			if (busy) return;
			approveMutation.mutate({ id: row.id });
		},
		[approveMutation, busy],
	);

	const handleReject = useCallback(
		(row: ScrubRow) => {
			if (busy) return;
			setModal({ id: row.id, conversion_id: row.conversion_id });
		},
		[busy],
	);

	const handleModalSubmit = useCallback(
		(reason: string, notes: string | null) => {
			if (!modal) return;
			rejectMutation.mutate({
				id: modal.id,
				reason,
				notes: notes ?? undefined,
			});
		},
		[modal, rejectMutation],
	);

	return (
		<div className="flex flex-col gap-6">
			<KpiStrip stats={stats} loading={queueQuery.isLoading} total={rows.length} />
			<QueueCard
				status={status}
				onStatusChange={setStatus}
				rows={rows}
				loading={queueQuery.isLoading}
				error={queueQuery.error?.message ?? null}
				busy={busy}
				onApprove={handleApprove}
				onReject={handleReject}
			/>
			{modal ? (
				<RejectReasonModal
					conversionId={modal.conversion_id}
					onCancel={() => setModal(null)}
					onSubmit={handleModalSubmit}
					submitting={rejectMutation.isPending}
				/>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Audit pane
// ---------------------------------------------------------------------------

function AuditPane() {
	const [status, setStatus] = useState<AuditFilter>("all");

	const auditQuery = useQuery({
		queryKey: scrubQueryKeys.audit(status),
		queryFn: () => fetchScrubAudit({ status }),
	});

	const rows: readonly ScrubRow[] = auditQuery.data?.items ?? [];

	return (
		<section className={`${CARD} flex flex-col`} data-testid="audit-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">Audit log</h2>
					<p className="text-sm text-zinc-500">
						{auditQuery.isLoading
							? "Loading review history."
							: rows.length === 0
								? "No reviewed conversions yet."
								: `${rows.length} reviewed conversion${rows.length === 1 ? "" : "s"} shown.`}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<label htmlFor="audit-status-filter" className={LABEL}>
						Status
					</label>
					<select
						id="audit-status-filter"
						name="auditStatus"
						className={SELECT}
						value={status}
						onChange={(e) => {
							const next = e.currentTarget.value;
							if (isAuditFilter(next)) setStatus(next);
						}}
						data-testid="audit-status-filter"
					>
						{AUDIT_FILTERS.map((f) => (
							<option key={f} value={f}>
								{f.charAt(0).toUpperCase() + f.slice(1)}
							</option>
						))}
					</select>
				</div>
			</header>

			{auditQuery.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{auditQuery.error.message}
				</div>
			) : rows.length === 0 && !auditQuery.isLoading ? (
				<div className="p-10 text-center text-sm text-zinc-500">
					No reviewed conversions for this filter.
				</div>
			) : (
				<AuditTable rows={rows} />
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// KPI strip
// ---------------------------------------------------------------------------

function KpiStrip(props: {
	stats: { today: number; week: number; pending: number; reviewed: number };
	loading: boolean;
	total: number;
}) {
	const cells = [
		{ label: "Today", value: props.stats.today },
		{ label: "This week", value: props.stats.week },
		{ label: "Pending", value: props.stats.pending },
		{ label: "Reviewed", value: props.stats.reviewed },
	];
	return (
		<section
			aria-label="Scrub statistics"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : formatInteger(c.value)}
					</p>
				</div>
			))}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Queue card / table
// ---------------------------------------------------------------------------

function QueueCard(props: {
	status: QueueFilter;
	onStatusChange: (next: QueueFilter) => void;
	rows: readonly ScrubRow[];
	loading: boolean;
	error: string | null;
	busy: boolean;
	onApprove: (row: ScrubRow) => void;
	onReject: (row: ScrubRow) => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="queue-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						{props.status === "pending"
							? "Pending conversions"
							: `${statusLabel(props.status as Exclude<QueueFilter, "all">)} conversions`}
					</h2>
					<p className="text-sm text-zinc-500">
						{props.rows.length === 0
							? "Queue is clear for this filter."
							: `${props.rows.length} conversion${props.rows.length === 1 ? "" : "s"} in view.`}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<label htmlFor="queue-status-filter" className={LABEL}>
						Status
					</label>
					<select
						id="queue-status-filter"
						name="queueStatus"
						className={SELECT}
						value={props.status}
						onChange={(e) => {
							const next = e.currentTarget.value;
							if (isQueueFilter(next)) props.onStatusChange(next);
						}}
						data-testid="queue-status-filter"
					>
						{QUEUE_FILTERS.map((f) => (
							<option key={f} value={f}>
								{f.charAt(0).toUpperCase() + f.slice(1)}
							</option>
						))}
					</select>
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading conversions.</div>
			) : props.rows.length === 0 ? (
				<EmptyQueueState status={props.status} />
			) : (
				<QueueTable
					rows={props.rows}
					busy={props.busy}
					onApprove={props.onApprove}
					onReject={props.onReject}
				/>
			)}
		</section>
	);
}

function QueueTable(props: {
	rows: readonly ScrubRow[];
	busy: boolean;
	onApprove: (row: ScrubRow) => void;
	onReject: (row: ScrubRow) => void;
}) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="queue-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>Scrub ID</Th>
						<Th>Conversion</Th>
						<Th>Status</Th>
						<Th>Opened</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{props.rows.map((row) => {
						const actionable = row.status === "pending";
						return (
							<tr
								key={row.id}
								data-testid={`queue-row-${row.id}`}
								className="hover:bg-zinc-50/80"
							>
								<Td mono>
									<span className="max-w-[9rem] truncate text-zinc-600">
										{row.id}
									</span>
								</Td>
								<Td mono>
									<span className="max-w-[14rem] truncate text-zinc-900">
										{row.conversion_id}
									</span>
								</Td>
								<Td>
									<StatusPill status={row.status} />
								</Td>
								<Td>{formatScrubDate(row.created_at)}</Td>
								<Td className="text-right">
									<div className="inline-flex gap-2">
										<button
											type="button"
											onClick={() => props.onApprove(row)}
											disabled={props.busy || !actionable}
											className={BTN_PRIMARY}
											data-testid={`approve-${row.id}`}
										>
											Approve
										</button>
										<button
											type="button"
											onClick={() => props.onReject(row)}
											disabled={props.busy || !actionable}
											className={BTN_DANGER}
											data-testid={`reject-${row.id}`}
										>
											Reject
										</button>
									</div>
								</Td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function EmptyQueueState(props: { status: QueueFilter }) {
	const hint =
		props.status === "pending"
			? "No pending conversions right now."
			: props.status === "approved"
				? "No approved conversions in this view."
				: props.status === "rejected"
					? "No rejected conversions on file."
					: "No scrub tickets to display.";
	return (
		<div className="flex flex-col items-center gap-1 p-10 text-center">
			<p className="text-sm font-medium text-zinc-700">{hint}</p>
			<p className="text-xs text-zinc-500">
				New scrub tickets appear here automatically as fraud signals fire.
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Audit table
// ---------------------------------------------------------------------------

function AuditTable(props: { rows: readonly ScrubRow[] }) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="audit-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>Conversion</Th>
						<Th>Status</Th>
						<Th>Reviewer</Th>
						<Th>Reviewed</Th>
						<Th>Reason</Th>
						<Th>Notes</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{props.rows.map((row) => (
						<tr
							key={row.id}
							data-testid={`audit-row-${row.id}`}
							className="hover:bg-zinc-50/80"
						>
							<Td mono>
								<span className="max-w-[14rem] truncate text-zinc-900">
									{row.conversion_id}
								</span>
							</Td>
							<Td>
								<StatusPill status={row.status} />
							</Td>
							<Td>{row.reviewed_by ?? "—"}</Td>
							<Td>{formatScrubDateTime(row.reviewed_at)}</Td>
							<Td>
								<span className="line-clamp-2 max-w-[18rem] text-zinc-700">
									{row.reason ?? "—"}
								</span>
							</Td>
							<Td>
								<span className="line-clamp-2 max-w-[18rem] text-zinc-700">
									{row.notes ?? "—"}
								</span>
							</Td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Reject reason modal — local to this file to stay inside the feature lane.
// ---------------------------------------------------------------------------

function RejectReasonModal(props: {
	conversionId: string;
	onCancel: () => void;
	onSubmit: (reason: string, notes: string | null) => void;
	submitting: boolean;
}) {
	const [reason, setReason] = useState("");
	const [notes, setNotes] = useState("");
	const [error, setError] = useState<string | null>(null);
	const reasonRef = useRef<HTMLTextAreaElement | null>(null);

	useEffect(() => {
		reasonRef.current?.focus();
	}, []);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const check = isReasonValid(reason);
		if (!check.ok) {
			setError(check.error);
			return;
		}
		setError(null);
		props.onSubmit(reason.trim(), notes.trim() ? notes.trim() : null);
	};

	return (
		<div
			className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 p-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby="reject-modal-title"
			data-testid="reject-modal"
			onClick={(e) => {
				if (e.target === e.currentTarget) props.onCancel();
			}}
		>
			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex w-full max-w-lg flex-col gap-4 p-5`}
			>
				<div className="flex flex-col gap-1">
					<h3
						id="reject-modal-title"
						className="text-base font-semibold text-zinc-900"
					>
						Reject conversion
					</h3>
					<p className="text-sm text-zinc-500">
						Conversion{" "}
						<span className="font-mono text-zinc-700">
							{props.conversionId}
						</span>
						. This decision is recorded in the audit log.
					</p>
				</div>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="reject-reason" className={LABEL}>
						Reason <span className="text-red-600">*</span>
					</label>
					<textarea
						ref={reasonRef}
						id="reject-reason"
						name="reason"
						rows={3}
						className={TEXTAREA}
						placeholder="e.g. Device farm pattern — 42 clicks in 3 minutes"
						value={reason}
						onChange={(e) => setReason(e.currentTarget.value)}
						data-testid="reject-reason-input"
						maxLength={1000}
						required
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="reject-notes" className={LABEL}>
						Notes <span className="text-zinc-400">(optional)</span>
					</label>
					<textarea
						id="reject-notes"
						name="notes"
						rows={2}
						className={TEXTAREA}
						placeholder="Optional analyst notes."
						value={notes}
						onChange={(e) => setNotes(e.currentTarget.value)}
						data-testid="reject-notes-input"
						maxLength={2000}
					/>
				</div>
				{error ? (
					<p className="text-sm text-red-600" role="alert">
						{error}
					</p>
				) : null}
				<div className="flex justify-end gap-2">
					<button
						type="button"
						onClick={props.onCancel}
						className={BTN_NEUTRAL}
						disabled={props.submitting}
						data-testid="reject-cancel"
					>
						Cancel
					</button>
					<button
						type="submit"
						className={BTN_DANGER}
						disabled={props.submitting}
						data-testid="reject-submit"
					>
						{props.submitting ? "Rejecting." : "Reject conversion"}
					</button>
				</div>
			</form>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------

function StatusPill(props: { status: ScrubRow["status"] }) {
	const tone =
		props.status === "approved"
			? "border-emerald-200 bg-emerald-50 text-emerald-800"
			: props.status === "rejected"
				? "border-red-200 bg-red-50 text-red-800"
				: "border-zinc-200 bg-zinc-100 text-zinc-700";
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${tone}`}
		>
			{statusLabel(props.status)}
		</span>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th
			scope="col"
			className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}
		>
			{props.children}
		</th>
	);
}

function Td(props: {
	children: React.ReactNode;
	className?: string;
	mono?: boolean;
}) {
	const base = "px-4 py-3 align-top text-zinc-700";
	const cls = [base, props.mono ? "font-mono text-xs" : "", props.className ?? ""]
		.filter(Boolean)
		.join(" ");
	return <td className={cls}>{props.children}</td>;
}

// ---------------------------------------------------------------------------
// Cache surgery — drop the just-reviewed row from the currently-visible filter
// so the UI reflects the decision instantly without waiting for a refetch.
// ---------------------------------------------------------------------------

function removeRowLocally(
	queryClient: ReturnType<typeof useQueryClient>,
	status: QueueFilter,
	removedId: string,
): void {
	queryClient.setQueryData<
		{ items: ScrubRow[]; count: number; filters: unknown } | undefined
	>(scrubQueryKeys.queue(status), (prev) => {
		if (!prev) return prev;
		const next = prev.items.filter((row) => row.id !== removedId);
		return { ...prev, items: next, count: next.length };
	});
}
